import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import heroVideo from "../assests/video/HERO.mp4";
import { cachedFetchJson } from "../utils/apiCache";

const getVideoId = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/shorts/")) {
      return parsed.pathname.split("/shorts/")[1]?.split("/")[0] || "";
    }
    if (parsed.searchParams.get("v")) return parsed.searchParams.get("v");
    const parts = parsed.pathname.split("/");
    return parts[parts.length - 1] || "";
  } catch {
    if (url.includes("shorts/")) return url.split("shorts/")[1]?.split("?")[0] || "";
    if (url.includes("watch?v=")) return url.split("watch?v=")[1]?.split("&")[0] || "";
    return "";
  }
};
const EMBED_HOST = "https://www.youtube.com";
const Herosection = () => {
  const navigate = useNavigate();
  const heroIframeRef = useRef(null);
  const [banners, setBanners] = useState([]);
  const [activeLaptop, setActiveLaptop] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [bannerError, setBannerError] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [clientsValue, setClientsValue] = useState(0);
  const [supportValue, setSupportValue] = useState(0);
  const [stats, setStats] = useState({
    experience: 0,
    brands: 0,
    solutions: 0,
    customers: 0,
  });
  const [reels, setReels] = useState([]);
  const [isReelLoading, setIsReelLoading] = useState(true);
  const [reelError, setReelError] = useState(null);
  const [testimonialImages, setTestimonialImages] = useState([]);
  const [testimonialCount, setTestimonialCount] = useState(0);
  const embedOrigin = typeof window !== "undefined" ? window.location.origin : "";

  const forceHeroPlay = () => {
    const frame = heroIframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: "playVideo", args: [] }),
      "*",
    );
    frame.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: "mute", args: [] }),
      "*",
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLaptop((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    if (banners.length === 0) return;
    if (activeLaptop >= banners.length) setActiveLaptop(0);
  }, [activeLaptop, banners.length]);

  useEffect(() => {
    let cancelled = false;
    let requestUrl = "";

    const loadBanners = async () => {
      try {
        setIsLoading(true);
        setBannerError(null);

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const baseUrl = (backendUrl || apiBase || backendRoot || "http://localhost:8000")
          .replace(/\/$/, "");

        const paths = process.env.REACT_APP_BANNERS_PATH
          ? [process.env.REACT_APP_BANNERS_PATH]
          : ["/banner", "/banners"];

        let data = null;
        for (const path of paths) {
          requestUrl = `${baseUrl}${path}`;
          try {
            data = await cachedFetchJson(requestUrl, { ttlMs: 120000 });
            break;
          } catch {
            data = null;
          }
        }

        if (!data) {
          throw new Error("Request failed");
        }
        const assetBase = (
          process.env.REACT_APP_BANNER_ASSET_BASE_URL ||
          process.env.REACT_APP_ASSET_BASE_URL ||
          backendRoot ||
          "http://localhost:8000"
        ).replace(/\/$/, "");
        const assetPath = (process.env.REACT_APP_BANNER_ASSET_PATH || "/banner")
          .replace(/\/$/, "")
          .replace(/^(?!\/)/, "/");

        const dataArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];

        const normalized = dataArray
          .map((item) => {
            const image = item.image
              ? item.image.startsWith("http")
                ? item.image
                : `${assetBase}${assetPath}/${String(item.image).replace(/^\/+/, "")}`
              : "";

            return {
              id: item.id,
              productId: item.product_id ?? null,
              brand: "PC CARE",
              model: item.name || "New Arrival",
              desc: item.description || "",
              selling_price: item.selling_price ?? null,
              original_price: item.original_price ?? null,
              shipping_charge: item.shipping_charge ?? null,
              rating: item.rating ?? null,
              stock_quantity: item.stock_quantity ?? null,
              screen_size: item.screen_size ?? null,
              stock: item.stock ?? null,
              specs: {
                processor: item.processor || "",
                ram: item.ram || "",
                storage: item.storage || "",
              },
              performance: {
                battery: item.battery_life || "",
                weight: item.weight || "",
                gpu: item.gpu || "",
              },
              image: image || "",
            };
          })
          .filter((item) => item.model);

        if (cancelled) return;
        if (normalized.length > 0) {
          setBanners(normalized);
        } else {
          setBanners([]);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching banners:", err);
          setBannerError(err.message || "Unable to load banners");
          setBanners([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadBanners();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let requestUrl = "";

    const loadTestimonials = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const baseUrl = (backendUrl || apiBase || backendRoot || "http://localhost:5000")
          .replace(/\/$/, "");
        const defaultPath = backendUrl ? "/testimonials" : "/api/testimonials";
        const path = process.env.REACT_APP_TESTIMONIALS_PATH || defaultPath;
        const params = new URLSearchParams({ status: "active", limit: "6" });
        requestUrl = `${baseUrl}${path}?${params.toString()}`;
        const data = await cachedFetchJson(requestUrl, { ttlMs: 120000 });
        const assetBase = (
          process.env.REACT_APP_ASSET_BASE_URL ||
          backendRoot ||
          "http://localhost:8000"
        ).replace(/\/$/, "");
        const imageBase = `${assetBase}/uploads`;
        const dataArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];

        const normalized = dataArray
          .map((item) => ({
            image: item.image
              ? item.image.startsWith("http")
                ? item.image
                : `${imageBase}/${String(item.image).replace(/^\//, "")}`
              : "",
            message: item.message || "",
            name: item.name || "",
          }))
          .filter((item) => item.message && item.name);

        if (cancelled) return;
        setTestimonialImages(normalized.map((item) => item.image).filter(Boolean).slice(0, 3));
        setTestimonialCount(normalized.length);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching testimonials:", err, requestUrl);
          setTestimonialImages([]);
          setTestimonialCount(0);
        }
      }
    };

    loadTestimonials();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let requestUrl = "";

    const loadReels = async () => {
      try {
        setIsReelLoading(true);
        setReelError(null);

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const baseUrl = (backendUrl || apiBase || backendRoot || "http://localhost:5000")
          .replace(/\/$/, "");
        const defaultPath = backendUrl ? "/live-videos" : "/api/live-videos";
        const path = process.env.REACT_APP_LIVE_VIDEOS_PATH || defaultPath;
        const params = new URLSearchParams({ status: "active", limit: "12" });
        requestUrl = `${baseUrl}${path}?${params.toString()}`;
        const data = await cachedFetchJson(requestUrl, { ttlMs: 120000 });
        const dataArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const normalized = dataArray
          .map((item) => ({
            id: item.id,
            url: item.youtube_url || "",
            status: item.status || "active",
          }))
          .filter((item) => item.url);

        if (cancelled) return;
        setReels(normalized);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching reels:", err);
          setReelError(err.message || "Unable to load videos");
          setReels([]);
        }
      } finally {
        if (!cancelled) setIsReelLoading(false);
      }
    };

    loadReels();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let value = 0;
    const target = 4.9;
    const step = 0.1;
    const timer = setInterval(() => {
      value = Math.min(target, Number((value + step).toFixed(1)));
      setRatingValue(value);
      if (value >= target) clearInterval(timer);
    }, 60);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let value = 0;
    const target = 500;
    const step = 10;
    const timer = setInterval(() => {
      value = Math.min(target, value + step);
      setClientsValue(value);
      if (value >= target) clearInterval(timer);
    }, 40);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let value = 0;
    const target = 24;
    const step = 1;
    const timer = setInterval(() => {
      value = Math.min(target, value + step);
      setSupportValue(value);
      if (value >= target) clearInterval(timer);
    }, 60);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const targets = {
      experience: 18,
      brands: 20,
      solutions: 50,
      customers: 20000,
    };
    const steps = {
      experience: 1,
      brands: 1,
      solutions: 2,
      customers: 250,
    };
    const timer = setInterval(() => {
      setStats((prev) => {
        const next = {
          experience: Math.min(targets.experience, prev.experience + steps.experience),
          brands: Math.min(targets.brands, prev.brands + steps.brands),
          solutions: Math.min(targets.solutions, prev.solutions + steps.solutions),
          customers: Math.min(targets.customers, prev.customers + steps.customers),
        };
        const done =
          next.experience >= targets.experience &&
          next.brands >= targets.brands &&
          next.solutions >= targets.solutions &&
          next.customers >= targets.customers;
        if (done) clearInterval(timer);
        return next;
      });
    }, 40);
    return () => clearInterval(timer);
  }, []);

  const current = banners[activeLaptop];

  const goToBannerDetails = (bannerId) => {
    if (bannerId) {
      navigate(`/products2/${bannerId}?type=banner`);
    }
  };

  if (isLoading) {
    return (
      <section className="min-h-[40vh] flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading banners...</p>
      </section>
    );
  }

  if (!current) {
    return (
      <section className="min-h-[40vh] flex items-center justify-center">
        <p className="text-sm text-red-500">
          {bannerError || "No banner data available"}
        </p>
      </section>
    );
  }

  return (
    <section
      className="relative overflow-hidden bg-[#f8faff] py-10 sm:py-14 lg:py-18"
      data-rating={ratingValue}
      data-clients={clientsValue}
      data-support={supportValue}
      data-reels-count={reels.length}
      data-reels-loading={isReelLoading}
      data-reels-error={reelError || ""}
      style={{
        background:
          "radial-gradient(900px 520px at 70% 10%, rgba(227,233,255,0.6) 0%, rgba(248,250,255,0.98) 45%, #f8faff 100%)",
      }}
    >
      <style>{`
        :root {
          --pc-primary: #F36F21;
          --pc-primary-strong: #E63946;
          --pc-ink: #351F72;
          --pc-blue-1: #e3e9ff;
          --pc-blue-2: #d6e0ff;
          --pc-blue-3: #c6d3ff;
        }
        .hero-fade-in {
          animation: heroFade 0.8s ease forwards;
        }
        @keyframes heroFade {
          0% { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .float-hero {
          animation: floatHero 6s ease-in-out infinite;
        }
        @keyframes floatHero {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .why-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
          border: 1px solid rgba(31, 60, 136, 0.08);
          background: linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%);
        }
        .why-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 22px 36px rgba(31, 60, 136, 0.18);
          border-color: rgba(31, 60, 136, 0.18);
        }
        .why-icon {
          animation: iconFloat 2.6s ease-in-out infinite;
        }
        .why-icon-shell {
          box-shadow: 0 14px 26px rgba(31, 60, 136, 0.18);
        }
        .circle-video {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border-radius: 9999px;
          background: transparent;
        }
        .circle-video iframe,
        .circle-video video {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          border: 0;
        }
        .circle-video video {
          transform: translate(-50%, -50%);
          object-fit: cover;
        }
        .circle-video iframe {
          width: 190%;
          height: 190%;
          transform: translate(-50%, -50%);
          transform-origin: center;
        }
        .hero-orbit-track {
          position: absolute;
          inset: 0;
          pointer-events: none;
          animation: orbitSpin var(--orbit-duration, 32s) linear infinite;
        }
        .hero-orbit-node {
          --angle: 0deg;
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) rotate(var(--angle))
            translateY(calc(-1 * var(--orbit-radius, clamp(128px, 22vw, 198px))));
        }
        .hero-orbit-core {
          display: flex;
          height: 40px;
          width: 40px;
          align-items: center;
          justify-content: center;
          border-radius: 9999px;
          background: #ffffff;
          box-shadow: 0 12px 24px rgba(243, 111, 33, 0.24);
          animation: orbitCounter var(--orbit-duration, 32s) linear infinite;
        }
        .hero-orbit-icon {
          display: flex;
          height: 30px;
          width: 30px;
          align-items: center;
          justify-content: center;
          border-radius: 9999px;
          background: linear-gradient(135deg, #f36f21 0%, #e63946 100%);
          color: white;
        }
        .hero-orbit-core.mini {
          height: 28px;
          width: 28px;
          box-shadow: 0 10px 18px rgba(243, 111, 33, 0.22);
        }
        .hero-orbit-icon.mini {
          height: 20px;
          width: 20px;
          font-size: 10px;
        }
        .hero-ring-spin {
          animation: orbitSpin 24s linear infinite;
          transform-origin: center;
        }
        .hero-ring-bubble {
          position: absolute;
          left: 50%;
          top: -7px;
          transform: translateX(-50%);
          display: flex;
          height: 14px;
          width: 14px;
          align-items: center;
          justify-content: center;
          border-radius: 9999px;
          background: linear-gradient(135deg, #f36f21 0%, #e63946 100%);
          color: white;
          box-shadow: 0 8px 16px rgba(243, 111, 33, 0.28);
          font-size: 8px;
        }
        .hero-main-shell {
          position: relative;
          isolation: isolate;
          background: radial-gradient(
            75% 75% at 50% 50%,
            rgba(255, 255, 255, 0.98) 0%,
            rgba(243, 247, 255, 0.92) 60%,
            rgba(228, 236, 255, 0.7) 100%
          );
          box-shadow:
            0 18px 36px rgba(53, 31, 114, 0.14),
            0 10px 22px rgba(243, 111, 33, 0.12),
            inset 0 2px 12px rgba(255, 255, 255, 0.85);
        }
        .hero-main-glow {
          position: absolute;
          inset: -10px;
          border-radius: 9999px;
          background: radial-gradient(
            55% 55% at 50% 50%,
            rgba(243, 111, 33, 0.1) 0%,
            rgba(53, 31, 114, 0.05) 45%,
            rgba(53, 31, 114, 0) 100%
          );
          filter: blur(6px);
          z-index: 0;
        }
        @media (max-width: 1024px) {
          .hero-orbit-core {
            height: 34px;
            width: 34px;
          }
          .hero-orbit-icon {
            height: 26px;
            width: 26px;
          }
        }
        @media (max-width: 640px) {
          .hero-orbit-core {
            height: 28px;
            width: 28px;
          }
          .hero-orbit-icon {
            height: 20px;
            width: 20px;
          }
          .hero-orbit-icon i {
            font-size: 10px;
          }
          .hero-ring-bubble {
            height: 10px;
            width: 10px;
          }
          .hero-main-shell {
            box-shadow:
              0 14px 26px rgba(53, 31, 114, 0.12),
              0 6px 16px rgba(243, 111, 33, 0.12),
              inset 0 2px 10px rgba(255, 255, 255, 0.9);
          }
        }
        @keyframes orbitSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes orbitCounter {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(-360deg); }
        }
        @keyframes iconFloat {
          0% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
          100% { transform: translateY(0); }
        }
      `}</style>

      <div className="absolute -top-28 -right-20 hidden h-80 w-80 rounded-full bg-[#e2ecff] md:block"></div>
      <div className="absolute -bottom-24 left-10 hidden h-72 w-72 rounded-full bg-[#f3f7ff] md:block"></div>
      <div className="absolute -bottom-10 right-[58%] h-24 w-24 rounded-full border border-[#f3c7bd] hero-ring-spin sm:-bottom-20 sm:right-[36%] sm:h-44 sm:w-44">
        <span className="hero-ring-bubble" aria-hidden="true"></span>
      </div>
      <div className="absolute -top-10 right-8 h-20 w-20 rounded-full border border-[#f3c7bd] hero-ring-spin sm:-top-6 sm:right-2 sm:h-36 sm:w-36">
        <span className="hero-ring-bubble" aria-hidden="true"></span>
      </div>

      <div className="mx-auto w-full max-w-[1320px] px-3 sm:px-4">
        <div className="relative grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-14">
          <div className="hero-fade-in group text-left">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[#e3e9ff] bg-white/80 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-[#351F72] shadow-[0_10px_20px_rgba(31,60,136,0.08)] md:mx-0">
  <span className="h-2 w-2 rounded-full bg-[#F36F21]"></span>
India’s Trusted Since 2008</div>

<h1 className="mx-auto mt-4 max-w-[760px] text-left text-[30px] font-extrabold leading-[1.2] tracking-[-0.02em] text-[#351F72] sm:text-[42px] lg:text-[50px] md:mx-0">
  𝗣𝗖 𝗖𝗮𝗿𝗲 𝗖𝗼𝗺𝗽𝘂𝘁𝗲𝗿
  <br />
  <span className="text-[var(--pc-primary)]">
    Trusted Used Laptop Seller
  </span>
</h1>

<p className="mt-3 inline-flex rounded-full border border-[#ffd9c4] bg-[#fff6f1] px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-[var(--pc-primary)]">
  Any Brand, Any Problem 
</p>

<p className="mt-4 max-w-[600px] text-left text-sm font-medium leading-relaxed text-[#4b5876] line-clamp-3 transition-all duration-300 group-hover:line-clamp-none sm:line-clamp-none sm:text-base">
  Since 2008, PC Care has been helping students, professionals, and businesses find reliable laptops at fair prices. 
  Every device is properly checked before sale, so you get performance you can trust. 
  No false promises — just genuine products, transparent dealing, and dependable after-sales support.
</p>
 <div className="mt-6 flex items-center justify-center gap-2 md:justify-start">
 
   <button
     type="button"
     onClick={() => navigate("/products")}
     className="
       flex-1 md:flex-none
       inline-flex items-center justify-center
       rounded-full
       bg-gradient-to-r from-[#F36F21] to-[#E63946]
       px-4 sm:px-5 md:px-7
       py-2.5 sm:py-3
       text-xs sm:text-base md:text-lg
       font-semibold text-white
       shadow-[0_14px_28px_rgba(230,57,70,0.24)]
       transition duration-300
       hover:-translate-y-1
       whitespace-nowrap
     "
   >
     View Laptops
   </button>
 
   <button
     type="button"
     onClick={() => navigate("/services")}
     className="
       flex-1 md:flex-none
       inline-flex items-center justify-center
       rounded-full
       border border-[#d5ddf8]
       bg-white
       px-6 sm:px-5 md:px-6
       py-2.5 sm:py-3
       text-xs sm:text-base md:text-lg
       font-semibold text-[#351F72]
       transition duration-300
       hover:text-[var(--pc-primary-strong)]
       whitespace-nowrap
     "
   >
     Get Service Support
   </button>
 
 </div>

            <div className="mt-8">
               <div className="mt-4 grid grid-cols-3 gap-1.5 sm:gap-4">
                 {[
                   {
                     count: `${stats.experience}+`,
                     label: "Years Experience",
                     icon: (
                       <svg
                         className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                         viewBox="0 0 24 24"
                         fill="none"
                         stroke="currentColor"
                         strokeWidth="2"
                         strokeLinecap="round"
                         strokeLinejoin="round"
                         aria-hidden="true"
                       >
                        <path d="M12 6v6l4 2" />
                        <circle cx="12" cy="12" r="9" />
                      </svg>
                    ),
                  },
                   {
                     count: `${stats.brands}+`,
                     label: "Laptop Brands",
                     icon: (
                       <svg
                         className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                         viewBox="0 0 24 24"
                         fill="none"
                         stroke="currentColor"
                         strokeWidth="2"
                         strokeLinecap="round"
                         strokeLinejoin="round"
                         aria-hidden="true"
                       >
                        <rect x="3" y="4" width="18" height="12" rx="2" />
                        <path d="M2 20h20" />
                      </svg>
                    ),
                  },
                   {
                     count: `${stats.customers}+`,
                     label: "Customers",
                     icon: (
                       <svg
                         className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                         viewBox="0 0 24 24"
                         fill="none"
                         stroke="currentColor"
                         strokeWidth="2"
                         strokeLinecap="round"
                         strokeLinejoin="round"
                         aria-hidden="true"
                       >
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <path d="M20 8v6" />
                        <path d="M23 11h-6" />
                      </svg>
                    ),
                  },
                 ].map((item) => (
                   <div
                     key={item.label}
                     className="why-card group relative overflow-hidden rounded-xl border border-[#d9e4ff] bg-white/90 px-2 py-2 text-left shadow-[0_10px_20px_rgba(31,60,136,0.12)] sm:rounded-2xl sm:px-4 sm:py-4 sm:shadow-[0_14px_28px_rgba(31,60,136,0.12)]"
                   >
                     <div className="absolute -right-3 -top-3 h-10 w-10 rounded-full bg-[#f4f7ff] sm:-right-4 sm:-top-4 sm:h-14 sm:w-14"></div>
                     <div className="relative flex items-start justify-between gap-2">
                       <div className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-[#F36F21] to-[#E63946] text-white shadow-[0_8px_16px_rgba(230,57,70,0.25)] sm:h-9 sm:w-9 sm:rounded-xl sm:shadow-[0_10px_22px_rgba(230,57,70,0.28)]">
                         {item.icon}
                       </div>
                     </div>
                     <div className="relative mt-1.5 text-sm font-extrabold leading-none text-[#351F72] sm:mt-3 sm:text-2xl lg:text-[30px]">
                       {item.count}
                     </div>
                     <div className="relative mt-0.5 text-[9px] font-semibold tracking-[0.02em] text-[#425176] sm:mt-1 sm:text-xs lg:text-sm">
                       {item.label}
                     </div>
                   </div>
                 ))}
               </div>
            </div>
          </div>

          <div className="relative mt-4 flex w-full flex-col items-center justify-center gap-4 lg:mt-0 lg:w-auto lg:flex-row lg:items-center lg:justify-end lg:gap-0">
            <div className="hero-main-shell relative z-10 h-[280px] w-[280px] rounded-full sm:h-[410px] sm:w-[410px] lg:h-[500px] lg:w-[500px]">
              <div className="hero-main-glow"></div>
              <div className="absolute inset-0 rounded-full bg-[var(--pc-blue-1)]"></div>
              <div className="absolute inset-6 rounded-full bg-[var(--pc-blue-2)]"></div>
              <div className="absolute inset-12 rounded-full bg-[var(--pc-blue-3)]"></div>
              <div className="absolute inset-[58px] rounded-full bg-[#eef2ff] sm:inset-[88px] lg:inset-[106px]"></div>
              <div className="absolute inset-[70px] rounded-full bg-white/70 sm:inset-[104px] lg:inset-[124px]"></div>

              <div className="absolute inset-[46px] flex items-center justify-center rounded-full bg-white/80 p-[5px] shadow-[0_16px_34px_rgba(31,60,136,0.2)] ring-2 ring-white/90 sm:inset-[78px] sm:p-[6px] lg:inset-[96px]">
                {reels.length > 0 && getVideoId(reels[0].url) ? (
                  <div className="circle-video">
                    <iframe
                      ref={heroIframeRef}
                      onLoad={() => {
                        forceHeroPlay();
                        setTimeout(forceHeroPlay, 400);
                      }}
                      src={`${EMBED_HOST}/embed/${getVideoId(
                        reels[0].url,
                      )}?autoplay=1&mute=1&loop=1&playlist=${getVideoId(
                        reels[0].url,
                      )}&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&disablekb=1&fs=0&iv_load_policy=3&origin=${encodeURIComponent(
                        embedOrigin,
                      )}`}
                      title="Watch & Shop Video"
                      frameBorder="0"
                      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      style={{ pointerEvents: "none" }}
                    />
                  </div>
                ) : (
                  <div className="circle-video">
                    <video src={heroVideo} autoPlay loop muted playsInline />
                  </div>
                )}
              </div>

              <div
                className="hero-orbit-track"
                style={{ "--orbit-duration": "32s", "--orbit-radius": "clamp(126px, 24vw, 194px)" }}
              >
                <div className="hero-orbit-node" style={{ "--angle": "0deg" }}>
                  <div className="hero-orbit-core">
                    <div className="hero-orbit-icon" title="Trust">
                      <i className="fa-solid fa-shield-halved text-[14px]" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
                <div className="hero-orbit-node" style={{ "--angle": "72deg" }}>
                  <div className="hero-orbit-core">
                    <div className="hero-orbit-icon" title="Delivery">
                      <i className="fa-solid fa-truck-fast text-[14px]" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
                <div className="hero-orbit-node" style={{ "--angle": "144deg" }}>
                  <div className="hero-orbit-core">
                    <div className="hero-orbit-icon" title="Happy Customers">
                      <i className="fa-solid fa-face-smile text-[14px]" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
                <div className="hero-orbit-node" style={{ "--angle": "216deg" }}>
                  <div className="hero-orbit-core">
                    <div className="hero-orbit-icon" title="Warranty">
                      <i className="fa-solid fa-circle-check text-[14px]" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
                <div className="hero-orbit-node" style={{ "--angle": "288deg" }}>
                  <div className="hero-orbit-core">
                    <div className="hero-orbit-icon" title="Support">
                      <i className="fa-solid fa-headset text-[14px]" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute left-[-6%] top-[36%] z-30 hidden w-[158px] rounded-2xl bg-white/90 px-2.5 py-2 shadow-[0_18px_40px_rgba(15,23,42,0.12)] backdrop-blur-md sm:left-[-10%] sm:block sm:w-[170px] sm:px-3 sm:py-2.5 lg:left-[-12%]">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#90b6ff] text-white shadow-[0_10px_18px_rgba(53,31,114,0.18)]">
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
                    <path d="M6 5H4a3 3 0 0 0 3 5" />
                    <path d="M18 5h2a3 3 0 0 1-3 5" />
                    <path d="M12 11v4" />
                    <path d="M8 19h8" />
                    <path d="M10 15h4" />
                  </svg>
                </div>
                <div>
                  <p className="text-[13px] font-bold text-slate-800">20k+</p>
                  <p className="text-[10px] font-semibold text-slate-600">Happy Customers Across India</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-[6%] right-[-2%] z-30 hidden w-[158px] rounded-2xl bg-white/90 px-2.5 py-2 shadow-[0_18px_40px_rgba(15,23,42,0.12)] backdrop-blur-md sm:right-[-4%] sm:block sm:w-[170px] sm:px-3 lg:right-[-6%]">
              <div className="absolute -top-3 left-4 flex h-7 w-7 items-center justify-center rounded-full bg-[var(--pc-primary)] text-white shadow-[0_10px_18px_rgba(243,111,33,0.35)]">
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20.8 4.6c-1.4-1.5-3.6-1.5-5 0L12 8.4 8.2 4.6c-1.4-1.5-3.6-1.5-5 0-1.5 1.6-1.5 4.1 0 5.7l8.8 9.1 8.8-9.1c1.5-1.6 1.5-4.1 0-5.7z" />
                </svg>
              </div>

              <div className="pt-1">
                <p className="text-[12px] font-semibold text-slate-800">Our Clients Reviews</p>
              </div>

              <div className="mt-1.5 flex items-center justify-between">
                <div className="flex -space-x-2.5">
                  {testimonialImages.length > 0 ? (
                    testimonialImages.map((src, index) => (
                      <img
                        key={`${src}-${index}`}
                        src={src}
                        alt={`Review ${index + 1}`}
                        className="h-7 w-7 rounded-full border-2 border-white object-cover"
                      />
                    ))
                  ) : (
                    <>
                      <div className="h-7 w-7 rounded-full border-2 border-white bg-gradient-to-br from-amber-200 to-amber-400"></div>
                      <div className="h-7 w-7 rounded-full border-2 border-white bg-gradient-to-br from-rose-200 to-rose-400"></div>
                      <div className="h-7 w-7 rounded-full border-2 border-white bg-gradient-to-br from-slate-200 to-slate-400"></div>
                    </>
                  )}
                </div>
                <div className="text-[11px] font-semibold text-slate-500">
                  {testimonialCount > 0 ? `${testimonialCount}+` : "0"}
                </div>
              </div>
            </div>

            <div className="mt-1 grid w-full max-w-[320px] grid-cols-2 gap-3 sm:hidden">
              <div className="rounded-2xl bg-white/90 px-3 py-2 shadow-[0_14px_26px_rgba(15,23,42,0.1)]">
                <div className="mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#F36F21] to-[#E63946] text-white">
                  <i className="fa-solid fa-users text-[10px]" aria-hidden="true"></i>
                </div>
                <p className="text-[12px] font-bold text-slate-800">20k+</p>
                <p className="text-[10px] font-semibold text-slate-600">Happy Customers</p>
              </div>
              <div className="rounded-2xl bg-white/90 px-3 py-2 shadow-[0_14px_26px_rgba(15,23,42,0.1)]">
                <div className="mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#F36F21] to-[#E63946] text-white">
                  <i className="fa-solid fa-star text-[10px]" aria-hidden="true"></i>
                </div>
                <div className="mb-1 flex items-center justify-between">
                  <div className="flex -space-x-2">
                    {testimonialImages.length > 0 ? (
                      testimonialImages.map((src, index) => (
                        <img
                          key={`mobile-${src}-${index}`}
                          src={src}
                          alt={`Review ${index + 1}`}
                          className="h-5 w-5 rounded-full border border-white object-cover"
                        />
                      ))
                    ) : (
                      <>
                        <div className="h-5 w-5 rounded-full border border-white bg-gradient-to-br from-amber-200 to-amber-400"></div>
                        <div className="h-5 w-5 rounded-full border border-white bg-gradient-to-br from-rose-200 to-rose-400"></div>
                        <div className="h-5 w-5 rounded-full border border-white bg-gradient-to-br from-slate-200 to-slate-400"></div>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] font-bold text-slate-700">
                    {testimonialCount > 0 ? `${testimonialCount}+` : "0"}
                  </p>
                </div>
                <p className="text-[12px] font-bold text-slate-800">Reviews</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Herosection;

